

import React from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { Skeleton } from '@/shared/ui/skeleton';

/**
 * インテリジェントSkeletonローディング
 * 実際のユーザーテーブルの構造を完全に再現し、滑らかなローディング体験を提供
 */
export const UserTableSkeleton: React.FC<{
  rowCount?: number;
  showAnimation?: boolean;
}> = ({
  rowCount = 5,
  showAnimation = true
}) => {
    // 段階的表示のためのデータ行生成
    const skeletonRows = Array.from({ length: rowCount }, (_, i) => ({
      id: `skeleton-${i}`,
      // 各行で異なる幅を持つことで、よりリアルなローディング体験
      usernameWidth: `${60 + (i * 10) % 40}%`,
      nameWidth: `${50 + (i * 15) % 30}%`,
      animationDelay: showAnimation ? `${i * 50}ms` : '0ms',
    }));

    return (
      <div className="overflow-x-auto rounded-md border bg-card shadow-sm">
        <Table className="min-w-full">
          <TableCaption className="py-4 text-sm text-muted-foreground">
            <Skeleton className="h-4 w-32 mx-auto" />
          </TableCaption>
          <TableHeader>
            <TableRow className="border-b bg-muted/30">
              <TableHead className="py-3 px-4 font-semibold text-foreground whitespace-nowrap">
                <div className="flex items-center gap-2">
                  ユーザー名
                </div>
              </TableHead>
              <TableHead className="py-3 px-4 font-semibold text-foreground whitespace-nowrap">
                <div className="flex items-center gap-2">
                  氏名
                </div>
              </TableHead>
              <TableHead className="py-3 px-4 font-semibold text-foreground whitespace-nowrap">
                <div className="flex items-center gap-2">
                  権限
                </div>
              </TableHead>
              <TableHead className="py-3 px-4 font-semibold text-foreground whitespace-nowrap">
                <div className="flex items-center gap-2">
                  ステータス
                </div>
              </TableHead>
              <TableHead className="py-3 px-4 font-semibold text-foreground whitespace-nowrap">
                <div className="flex items-center gap-2">
                  登録日時
                </div>
              </TableHead>
              <TableHead className="py-3 px-4 text-right font-semibold text-foreground whitespace-nowrap">
                <div className="flex items-center gap-2 justify-end">
                  操作
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {skeletonRows.map((row) => (
              <TableRow
                key={row.id}
                className="border-b hover:bg-muted/20 transition-colors"
                style={{
                  animationDelay: row.animationDelay,
                  animation: showAnimation ? 'fadeIn 0.3s ease-in-out' : 'none'
                }}
              >
                {/* ユーザー名 */}
                <TableCell className="py-3 px-4 font-medium whitespace-nowrap">
                  <Skeleton
                    className="h-4"
                    style={{ width: row.usernameWidth }}
                  />
                </TableCell>

                {/* 氏名 */}
                <TableCell className="py-3 px-4 whitespace-nowrap">
                  <Skeleton
                    className="h-4"
                    style={{ width: row.nameWidth }}
                  />
                </TableCell>

                {/* 権限 */}
                <TableCell className="py-3 px-4 whitespace-nowrap">
                  <Skeleton className="h-5 w-16 rounded-full" />
                </TableCell>

                {/* ステータス */}
                <TableCell className="py-3 px-4 whitespace-nowrap">
                  <Skeleton className="h-5 w-12 rounded-full" />
                </TableCell>

                {/* 登録日時 */}
                <TableCell className="py-3 px-4 whitespace-nowrap">
                  <Skeleton className="h-4 w-32" />
                </TableCell>

                {/* 操作 */}
                <TableCell className="text-right py-3 px-4 whitespace-nowrap">
                  <div className="flex justify-end">
                    <Skeleton className="h-8 w-8 rounded-md" />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* カスタムアニメーション定義 */}
        <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      </div>
    );
  };

/**
 * 高度なSkeletonローディング with プレースホルダー情報
 * データ取得の進捗に応じて段階的に表示を切り替え
 * SWRキャッシュとの連携でより滑らかなUXを提供
 */
export const AdvancedUserTableSkeleton: React.FC<{
  isAuthLoading: boolean;
  isUsersLoading: boolean;
  userCount?: number;
  hasCachedData?: boolean;
}> = ({
  isAuthLoading,
  isUsersLoading,
  userCount = 0,
  hasCachedData = false
}) => {
    if (isAuthLoading) {
      // 認証確認中の場合、よりシンプルなローディング
      return (
        <div className="flex justify-center items-center h-64">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full animate-pulse" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24 opacity-60" />
            </div>
          </div>
        </div>
      );
    }

    if (isUsersLoading && !hasCachedData) {
      // 初回読み込み時（キャッシュなし）の場合、テーブル構造を保ったSkeleton
      return (
        <UserTableSkeleton
          rowCount={userCount > 0 ? userCount : 5}
          showAnimation={true}
        />
      );
    }

    if (isUsersLoading && hasCachedData) {
      // キャッシュ再検証中の場合、より控えめなローディング表示
      return (
        <div className="absolute top-4 right-4 z-10">
          <div className="flex items-center gap-2 px-3 py-2 bg-background/80 backdrop-blur-sm rounded-lg border shadow-sm">
            <Skeleton className="h-3 w-3 rounded-full animate-pulse" />
            <span className="text-xs text-muted-foreground">更新中...</span>
          </div>
        </div>
      );
    }

    return null;
  };

/**
 * 軽量なローディングインジケーター（SWR再検証用）
 */
export const MinimalLoadingIndicator: React.FC = () => (
  <div className="flex items-center justify-center py-2">
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Skeleton className="h-3 w-3 rounded-full animate-pulse" />
      <span>データを更新しています...</span>
    </div>
  </div>
);